import { AutofillMonitor } from '@angular/cdk/text-field';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild
} from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { DomEvents } from '../../../constants/dom-events.constants';
import { KeyCodes } from '../../../constants/keycodes.constants';
import { Regex } from '../../../constants/regex.constants';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';

// This is a slim version of the text input component with minimal styling that can be used
// as a building block to build more complex form controls.
// This is a WIP and any features that are in lsq-input can be ported over as needed.
@Component({
    selector: 'lsq-base-input',
    styleUrls: ['./base-input.component.scss'],
    template: `
        <div class="lsq-base-input" [class.has-error]="hasError">
            <label
                *ngIf="!!label"
                for="name"
                [class.inputFocusedOrFilled]="isInputFocused || value"
            >
                {{ label }}
            </label>
            <input
                #inputElement
                [id]="name"
                [class.has-float-label]="!!label"
                [placeholder]="placeholder && !label ? placeholder : ' '"
                type="text"
                [value]="value"
                [disabled]="disabled"
                [ngModel]="value"
                (ngModelChange)="handleValueChanged($event)"
                [ngModelOptions]="ngModelOptions"
                [autocomplete]="autocomplete"
                [maxlength]="maxLength"
                [textMask]="{ mask: getMask(mask), guide: false }"
                (focus)="setInputFocus(true)"
                (blur)="setInputFocus(false)"
                (keypress)="handleInternalKeyPress($event)"
            />
        </div>
    `
})
@LifecycleAware()
export class BaseInputComponent implements AfterViewInit, OnDestroy {
    @ViewChild('inputElement') public inputElement: ElementRef;

    @Input() public value: string;
    @Input() public type: string;
    @Input() public name: string;
    @Input() public label?: string; // label is only needed here for float label inputs.
    @Input() public placeholder?: string;
    @Input() public disabled = false;
    @Input() public autocomplete = 'new-password'; // Prevents autocomplete suggestions.
    @Input() public mask?: RegExp = null;
    @Input() public unmask?: RegExp = null;
    @Input() public hasError = false;
    @Input() public maxLength?: string;
    @Input() public digitsOnly = false;
    @Output() public valueChanged = new EventEmitter<{
        value: string;
        isAutofilled: boolean;
    }>();
    @Output() public blurred = new EventEmitter<undefined>();

    public isInputFocused = false;
    public isAutofilled = false;

    public readonly ngModelOptions = { updateOn: DomEvents.Change };

    constructor(private _autofillMonitor: AutofillMonitor) {}

    public ngAfterViewInit(): void {
        this._autofillMonitor
            .monitor(this.inputElement.nativeElement)
            .pipe(forLifeOf(this))
            .subscribe((event) => {
                this.isAutofilled = event.isAutofilled;
            });
    }

    public ngOnDestroy(): void {
        if (!!this.inputElement) {
            this._autofillMonitor.stopMonitoring(this.inputElement.nativeElement);
        }
    }

    public handleValueChanged(inputtedValue: string): void {
        let previousValue = this.value;
        let newValue = inputtedValue;

        if (this.digitsOnly) {
            newValue = newValue.replace(/[^0-9]/g, '');
            previousValue = previousValue.replace(/[^0-9]/g, '');
        }

        if (this.unmask) {
            newValue = newValue.replace(this.unmask, '');
        }

        // This ensures the DOM is updated if the value is the same as the previous.
        if (newValue === previousValue) {
            this.inputElement.nativeElement.value = newValue;
        }

        this.valueChanged.emit({
            value: newValue,
            isAutofilled: this.isAutofilled
        });
    }

    public setInputFocus(focused: boolean): void {
        this.isInputFocused = focused;

        if (!focused) {
            this.blurred.emit();
        }
    }

    public handleInternalKeyPress(event: KeyboardEvent): void {
        if (event.which === KeyCodes.Enter) {
            // If the user presses 'enter' force a blur for validation.
            this.blurred.emit();
            return;
        }
        if (this.digitsOnly) {
            // Only allow number keys.
            if (event.which < KeyCodes.Zero || event.which > KeyCodes.Nine) {
                event.preventDefault();
                return;
            }
        }
    }

    // Mask plugin does not handle null well so we have to pass it like this so it ignores the mask.
    public getMask(mask: Regex): Regex | false {
        return mask ?? false;
    }
}
